// Header above the right-side Formula / Experiment column. Four
// tabs cycle through the four saved-content lists:
//
//   My Experiments / My Formulas       — auth-gated; calls
//                                        /experiments and /formulas
//   Public Experiments / Public Formulas — open; calls
//                                        /experiments/public and
//                                        /formulas/public
//
// Clicking a row in the open list calls one of the load callbacks
// passed in by App, which routes the payload back into the right
// panel: experiments populate the Experiment JSON textarea,
// formulas populate the Formula source textarea. The selection
// state is local to this component — the panels just receive the
// new value through their existing controlled inputs.

import { useEffect, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import {
  ApiError,
  api,
  type SavedExperiment,
  type SavedFormula,
} from "../auth/client";

type Tab = "my-experiments" | "my-formulas" | "public-experiments" | "public-formulas";

const TABS: { id: Tab; label: string; auth: boolean; kind: "experiment" | "formula" }[] = [
  { id: "my-experiments", label: "My Experiments", auth: true, kind: "experiment" },
  { id: "my-formulas", label: "My Formulas", auth: true, kind: "formula" },
  { id: "public-experiments", label: "Public Experiments", auth: false, kind: "experiment" },
  { id: "public-formulas", label: "Public Formulas", auth: false, kind: "formula" },
];

export interface LibraryPanelProps {
  /** Called when the user clicks a saved experiment row. The payload
   * is the raw `payload` field from the row (already validated as
   * JSON server-side; we re-stringify in the loader to give the
   * panel a clean text representation). */
  onLoadExperiment: (row: SavedExperiment) => void;
  /** Called when the user clicks a saved formula row. */
  onLoadFormula: (row: SavedFormula) => void;
  /** Open the auth modal — used when the user picks one of the "My ..."
   * tabs while signed out. */
  onOpenAuth: (mode: "signin" | "signup") => void;
  /** Open the Courses modal. Sits above the saved-content tabs as
   *  the entry point into the bundled curriculum. */
  onOpenCourses: () => void;
  /** Open the Puzzles modal. Sits above the Courses button — a
   *  difficulty-sorted catalogue of partial-solution challenges. */
  onOpenPuzzles: () => void;
}

export function LibraryPanel(props: LibraryPanelProps) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab | null>(null);
  const [experiments, setExperiments] = useState<SavedExperiment[] | null>(null);
  const [formulas, setFormulas] = useState<SavedFormula[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refetch whenever the active tab changes. Local lists are
  // discarded on tab switch so a stale "My Experiments" view
  // doesn't bleed into "Public Experiments" by accident.
  useEffect(() => {
    if (!activeTab) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setExperiments(null);
    setFormulas(null);

    const tab = TABS.find((t) => t.id === activeTab)!;
    if (tab.auth && !user) {
      // Authed tab clicked while signed out: defer the network call
      // and prompt for signin instead. Closing the modal without
      // signing in leaves the tab open but empty.
      setLoading(false);
      props.onOpenAuth("signin");
      return () => {
        cancelled = true;
      };
    }

    const fetcher = async () => {
      try {
        if (activeTab === "my-experiments") {
          const r = await api.listExperiments();
          if (!cancelled) setExperiments(r.experiments);
        } else if (activeTab === "public-experiments") {
          const r = await api.listPublicExperiments();
          if (!cancelled) setExperiments(r.experiments);
        } else if (activeTab === "my-formulas") {
          const r = await api.listFormulas();
          if (!cancelled) setFormulas(r.formulas);
        } else {
          const r = await api.listPublicFormulas();
          if (!cancelled) setFormulas(r.formulas);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          props.onOpenAuth("signin");
          setError("Sign in to view this list.");
        } else {
          setError(err instanceof Error ? err.message : "Could not load list");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetcher();
    return () => {
      cancelled = true;
    };
    // `props.onOpenAuth` is stable across renders in App; intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user]);

  const onTabClick = (id: Tab) => {
    // Click an active tab again to close it.
    setActiveTab((current) => (current === id ? null : id));
  };

  return (
    <div className="library-panel">
      <button
        type="button"
        className="library-puzzles-button"
        onClick={props.onOpenPuzzles}
        title="Open the puzzles catalogue"
        aria-label="Open puzzles"
      >
        {/* Four-piece puzzle glyph (kept emoji-free per project
         * style). Inline SVG so the icon inherits currentColor and
         * matches the rest of the toolbar. */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          className="library-puzzles-icon"
        >
          <path
            fill="currentColor"
            d="M2 2h4v2.5a1.5 1.5 0 0 0 3 0V2h5v5h-2.5a1.5 1.5 0 0 0 0 3H14v4H9v-2.5a1.5 1.5 0 0 0-3 0V14H2V9h2.5a1.5 1.5 0 0 0 0-3H2V2z"
          />
        </svg>
        <span>Puzzles</span>
        <span className="library-puzzles-hint">Easy → Hyper-extreme</span>
      </button>
      <button
        type="button"
        className="library-courses-button"
        onClick={props.onOpenCourses}
        title="Open the courses book"
        aria-label="Open courses"
      >
        {/* Open-book glyph (no emoji per project style). */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          className="library-courses-icon"
        >
          <path
            fill="currentColor"
            d="M1 3h5a2 2 0 0 1 2 2v8a2 2 0 0 0-2-2H1V3zm14 0h-5a2 2 0 0 0-2 2v8a2 2 0 0 1 2-2h5V3z"
          />
        </svg>
        <span>Courses</span>
        <span className="library-courses-hint">Starter → Genius</span>
      </button>
      <div className="library-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={"library-tab" + (activeTab === tab.id ? " active" : "")}
            onClick={() => onTabClick(tab.id)}
            title={tab.auth ? "Requires sign-in" : "Open to anyone"}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab !== null && (
        <div className="library-list">
          {loading && <div className="library-status">Loading…</div>}
          {error && <div className="library-status library-error">{error}</div>}
          {!loading && !error && experiments !== null && (
            <ExperimentList rows={experiments} onPick={props.onLoadExperiment} />
          )}
          {!loading && !error && formulas !== null && (
            <FormulaList rows={formulas} onPick={props.onLoadFormula} />
          )}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Local-time short form: "Apr 27, 14:32". Public lists span any
  // timezone, but a local timestamp is closer to "when did I see
  // this" than a UTC one would be.
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface ExperimentListProps {
  rows: SavedExperiment[];
  onPick: (row: SavedExperiment) => void;
}

function ExperimentList({ rows, onPick }: ExperimentListProps) {
  if (rows.length === 0) {
    return <div className="library-empty">Nothing here yet.</div>;
  }
  return (
    <ul className="library-rows">
      {rows.map((row) => (
        <li key={row.id}>
          <button
            type="button"
            className="library-row"
            onClick={() => onPick(row)}
            title={`Load ${row.name}`}
          >
            <span className="library-row-name">{row.name}</span>
            <span className="library-row-meta">
              {row.is_public ? "public · " : ""}
              {formatDate(row.created_at)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

interface FormulaListProps {
  rows: SavedFormula[];
  onPick: (row: SavedFormula) => void;
}

function FormulaList({ rows, onPick }: FormulaListProps) {
  if (rows.length === 0) {
    return <div className="library-empty">Nothing here yet.</div>;
  }
  return (
    <ul className="library-rows">
      {rows.map((row) => (
        <li key={row.id}>
          <button
            type="button"
            className="library-row"
            onClick={() => onPick(row)}
            title={`Load formula`}
          >
            <span className="library-row-name">{row.source}</span>
            <span className="library-row-meta">
              {row.is_public ? "public · " : ""}
              {formatDate(row.created_at)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
